import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    addPhoto,
    getActiveSession,
    getSessionPhotosForTarget,
    removePhoto,
} from "../offline/sessionManager";
import { callAvosApi } from "../utils/useAvosApiHook";

export interface FruitImage {
    image_id: number;
    type: string;
    url?: string;
    url_webp?: string;
    base64?: string;
    focusMode?: any;
    [key: string]: any;
}

/**
 * If there's an active offline session AND the avocadoId is one of
 * its temp ids, return the photos from the session. Otherwise return
 * null and let the caller fall back to the server query.
 *
 * Resolves the session (rather than just its id) because the pointer
 * outlives the row once replay deletes it; a dead id would return []
 * here and permanently mask the server's images.
 */
async function readOfflineFruitImages(
    avocadoId: number | undefined,
): Promise<FruitImage[] | null> {
    if (!avocadoId) return null;
    const session = await getActiveSession();
    if (!session) return null;
    const photos = await getSessionPhotosForTarget(session.id, "fruit", avocadoId);
    if (photos.length === 0) {
        // No photos yet on this offline fruit. Returning [] (vs null)
        // tells the caller "I handled it; don't query the server" —
        // important so the server query doesn't 404 against a temp id.
        return [];
    }
    return photos;
}

// Hook to get fruit images
export const useFruitImages = (tenantId: string | undefined, avocadoId: number | undefined) => {
    return useQuery({
        // networkMode: "always" so the queryFn runs while offline. With the
        // default "online", TanStack pauses the query when navigator.onLine is
        // false and never calls queryFn — so the IndexedDB read below is
        // skipped, the captured photo never reaches the form/modal, and no
        // preview renders. It also lets invalidateQueries() after a capture
        // actually refetch. The matching mutation already uses "always".
        networkMode: "always",
        queryKey: ["fruitImages", tenantId, avocadoId],
        queryFn: async (): Promise<FruitImage[]> => {
            // Offline path: if this avocadoId belongs to an active
            // offline session, serve photos from IndexedDB and skip
            // the server call entirely (the temp id has no
            // server-side row).
            const offlinePhotos = await readOfflineFruitImages(avocadoId);
            if (offlinePhotos !== null) {
                return offlinePhotos;
            }
            const { data } = await callAvosApi(`/${tenantId}/images/fruit/${avocadoId}/images`);
            return data || [];
        },
        enabled: !!tenantId && !!avocadoId && avocadoId > 0,
    });
};

// Hook to add a fruit image
export const useAddFruitImage = () => {
    const queryClient = useQueryClient();

    return useMutation({
        // Run the mutationFn whether navigator.onLine is true or false.
        // TanStack Query's default `networkMode: "online"` pauses mutations
        // while offline — which would skip our `if (getActiveSessionId())`
        // session-write branch entirely. With "always", the fn runs and
        // takes the offline branch.
        networkMode: "always",
        mutationFn: async ({ tenantId, avocadoId, type, base64, focusMode }: {
            tenantId: string;
            avocadoId: number;
            type: string;
            base64: string;
            focusMode: any;
        }) => {
            // Offline path: append to the active session and return
            // the synthetic image record so the cache update below
            // makes the form re-render. Resolve the session, not just its
            // id — addPhoto throws on a deleted one, which the camera modal
            // reports as "Failed to save Image".
            const session = await getActiveSession();
            if (session) {
                const { syntheticImage } = await addPhoto(session.id, {
                    type,
                    target: "fruit",
                    target_temp_id: avocadoId,
                    base64,
                    focusMode,
                });
                return syntheticImage;
            }
            const { data } = await callAvosApi(`/${tenantId}/images/fruit/${avocadoId}/image/`, {
                method: "POST",
                data: { avocado_id: avocadoId, type, base64, focusMode }
            });
            return data;
        },
        onSuccess: (data, variables) => {
            // Invalidate and refetch fruit images after adding
            queryClient.invalidateQueries({ queryKey: ["fruitImages", variables.tenantId, variables.avocadoId] });
        },
    });
};

// Hook to delete a fruit image
export const useDeleteFruitImage = () => {
    const queryClient = useQueryClient();

    return useMutation({
        // See useAddFruitImage for the networkMode: "always" rationale —
        // the offline branch only runs if the mutationFn isn't paused.
        networkMode: "always",
        mutationFn: async ({ tenantId, avocadoId, image }: {
            tenantId: string;
            avocadoId: number;
            image: FruitImage;
        }) => {
            const session = await getActiveSession();
            if (session) {
                // Offline: drop the photo from the session by its
                // ordinal position within the same target.
                const photos = await getSessionPhotosForTarget(session.id, "fruit", avocadoId);
                const idx = photos.findIndex((p) => p.image_id === image.image_id);
                if (idx >= 0) {
                    await removePhoto(session.id, "fruit", avocadoId, idx);
                }
                return { ok: true };
            }
            const { data } = await callAvosApi(`/${tenantId}/images/fruit/${avocadoId}/image/${image.image_id}`, {
                method: "DELETE",
                data: image
            });
            return data;
        },
        onSuccess: (data, variables) => {
            // Invalidate and refetch fruit images after deleting
            queryClient.invalidateQueries({ queryKey: ["fruitImages", variables.tenantId, variables.avocadoId] });
        },
    });
};

